import { neo4jUrl, neo4jUsername, neo4jPassword } from '@/config/env';
import type { GraphDbType } from '@/interfaces/systemConfig.interface';

// Pagination constants for table views
export const DEFAULT_PAGE = 0;
export const DEFAULT_ROWS_PER_PAGE = 20;
export const DEFAULT_ENTITY_NODES_LIMIT = 1000; // Default limit for non-paginated entity node queries
export const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

export const SCHEMA_CYPHER_QUERY = `
  CALL db.schema.visualization()
`;

// Memgraph does not support `CALL db.schema.visualization()`. `SHOW SCHEMA INFO`
// returns a JSON object with `nodes` and `edges` arrays and works when the
// backend runs it in an implicit (auto-commit) transaction. The response is
// normalized client-side by `buildSchemaGraphFromShowSchemaInfo`.
export const MEMGRAPH_SCHEMA_CYPHER_QUERY = `SHOW SCHEMA INFO`;

// `db.schema.visualization()` does not expose per-label property keys, so the
// Neo4j schema property metadata (`propertiesByLabel`) is fetched with a
// separate plain query. `db.schema.nodeTypeProperties()` returns one row per
// (nodeLabels, propertyName) pair, normalized client-side into
// `Record<label, string[]>` by `buildPropertiesByLabelFromNodeTypeRows`.
// `propertyTypes` comes along so the Analysis authoring form can offer only
// the fields a metric can actually summarize; Memgraph reports the equivalent
// inside `SHOW SCHEMA INFO`.
export const NEO4J_SCHEMA_PROPERTIES_CYPHER_QUERY = `
  CALL db.schema.nodeTypeProperties()
  YIELD nodeLabels, propertyName, propertyTypes
  RETURN nodeLabels, propertyName, propertyTypes
`;

/**
 * Returns the schema cypher query that matches the active graph database
 * engine. Defaults to the Neo4j query when the type is unknown so existing
 * deployments keep working unchanged.
 */
export const getSchemaCypherQuery = (
  graphDbType?: GraphDbType | null,
): string =>
  graphDbType === 'memgraph'
    ? MEMGRAPH_SCHEMA_CYPHER_QUERY
    : SCHEMA_CYPHER_QUERY;

/** Memgraph schema rows are returned in plain mode and normalized client-side. */
export const getSchemaQueryMode = (
  graphDbType?: GraphDbType | null,
): QueryMode =>
  graphDbType === 'memgraph' ? QUERY_MODE.PLAIN : QUERY_MODE.GRAPH;

export const isMemgraphSchemaCypherQuery = (query: string): boolean =>
  query === MEMGRAPH_SCHEMA_CYPHER_QUERY;

/**
 * True when the provided cypher matches any of the supported schema queries.
 * Used to branch result formatting (schema vs. regular graph results).
 */
export const isSchemaCypherQuery = (query: string): boolean =>
  query === SCHEMA_CYPHER_QUERY || query === MEMGRAPH_SCHEMA_CYPHER_QUERY;

export const NODE_RELATIONSHIP_CYPHER_QUERY = `
  MATCH (n:%label%)-[r]->(m)
  WHERE elementId(n) = $nodeId
  RETURN labels(m)[0] AS nodeType, count(m) AS count
  UNION
  MATCH (n:%label%)<-[r]-(m)
  WHERE elementId(n) = $nodeId
  RETURN labels(m)[0] AS nodeType, count(m) AS count;
`;

export const MEMGRAPH_NODE_RELATIONSHIP_CYPHER_QUERY = `
  MATCH (n:%label%)-[r]->(m)
  WHERE id(n) = toInteger($nodeId)
  RETURN labels(m)[0] AS nodeType, count(m) AS count
  UNION
  MATCH (n:%label%)<-[r]-(m)
  WHERE id(n) = toInteger($nodeId)
  RETURN labels(m)[0] AS nodeType, count(m) AS count;
`;

/**
 * Returns the node-relationship count query for the active graph engine.
 * Memgraph uses internal numeric ids via `id()`; Neo4j uses `elementId()`.
 */
export const getNodeRelationshipCypherQuery = (
  graphDbType?: GraphDbType | null,
): string =>
  graphDbType === 'memgraph'
    ? MEMGRAPH_NODE_RELATIONSHIP_CYPHER_QUERY
    : NODE_RELATIONSHIP_CYPHER_QUERY;

// %orderByClause% is replaced with "" or " ORDER BY n[$sortField] ASC/DESC"
// %whereClause% is replaced with "" or " WHERE <predicates>" for server-side filtering
export const ENTITY_NODES_CYPHER_QUERY = `
  MATCH (n:%entityType%)
  %whereClause%
  WITH n
  %orderByClause%
  RETURN n
  SKIP $skip
  LIMIT $limit
`;

export const neo4jCredentials = {
  url: neo4jUrl,
  username: neo4jUsername,
  password: neo4jPassword,
};

// Query mode constants
export const QUERY_MODE = {
  GRAPH: 'graph',
  PLAIN: 'plain',
} as const;

export type QueryMode = (typeof QUERY_MODE)[keyof typeof QUERY_MODE];

/**
 * Properties that name a record, most specific first.
 *
 * One list, because a record must be called the same thing everywhere it
 * appears. The search panel and the canvas each grew their own: the panel read
 * `display_name` while the canvas read only `name`/`product_name`/
 * `contract_terms`, so an Identity opened from a search was captioned in the
 * results list and anonymous on the graph — and after a reload, where the node
 * is rebuilt from the pipeline rather than carried over from the hit, that was
 * the only caption it had.
 */
export const NODE_NAME_PROPERTIES = [
  'name',
  'display_name',
  'title',
  'identifier',
  'label',
  'code',
  // Kept from the canvas's own former list: these name records in projects
  // that predate `display_name`, and dropping them would rename those nodes.
  'product_name',
  'contract_terms',
] as const;
